import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import tokens from '@verifime/design-tokens';
import { TIdentityDocumentType, usePolling } from '@verifime/utils';
import { useEffect, useState } from 'react';
import {
  TApiScanIdentityDocument,
  TApiScanIdentityDocumentResponse,
  TScanMutationId,
} from './types';

const LoadingIndicator = () => {
  return (
    <Stack direction="row" gap={tokens.spacingBase}>
      <CircularProgress color="inherit" size={20} />
      <Typography>Extracting information from your ID</Typography>
    </Stack>
  );
};

type TScanIdentityDocumentProps = Readonly<{
  customerId: string;
  documentType: TIdentityDocumentType;
  scanFilesId: string | null;
  scanMutationId: TScanMutationId;
  API: {
    scanIdentityDocument: TApiScanIdentityDocument;
  };
  dataTestId: string;
  onScanSucceeded: (data: TApiScanIdentityDocumentResponse) => void;
  onScanFailed: () => void;
  maxRetry?: number;
  pollingIntervalInMs?: number;
}>;

export default function ScanIdentityDocument({
  customerId,
  documentType,
  scanFilesId,
  scanMutationId,
  API,
  dataTestId,
  onScanSucceeded,
  onScanFailed,
  maxRetry = 30,
  pollingIntervalInMs = 1000,
}: TScanIdentityDocumentProps) {
  const [isScanning, setIsScanning] = useState(false);

  const { startPolling, stopPolling } = usePolling({
    autoStart: false,
    pollingAction: () => {
      if (!scanFilesId) return;
      if (documentType === 'OtherIdentityDocument') {
        stopPolling();
        return;
      }
      setIsScanning(true);

      return API.scanIdentityDocument({
        customerId,
        documentType,
        scanFilesId,
      })
        .then((data) => {
          if (data) {
            onScanSucceeded?.(data);
            stopPolling();
            setIsScanning(false);
          }
        })
        .catch((error) => {
          console.error('Error scanning identity document: ', error);
          onScanFailed?.();
          setIsScanning(false);
        });
    },
    pollingIntervalInMs,
    maxRetry,
    onMaxRetryReached: () => {
      setIsScanning(false);
      onScanFailed();
    },
  });

  useEffect(() => {
    if (scanMutationId) {
      if (scanMutationId.action === 'CANCEL') {
        stopPolling();
        setIsScanning(false);
        return;
      }
      if (scanMutationId.action === 'SCAN') {
        startPolling();
      }
    }
    return () => {
      stopPolling();
    };
  }, [scanMutationId, startPolling, stopPolling]);

  if (isScanning) {
    return (
      <Box data-testid={dataTestId} sx={{ width: '100%' }}>
        <LoadingIndicator />
      </Box>
    );
  }
  return null;
}
